/**
 * Shared API client (base URL + fetch helper).
 * Silent refresh on 401 (access+refresh cookies, like KADR).
 */

export const API_URL = '/api/v1';

let refreshInFlight: Promise<boolean> | null = null;

/** Rotate access via HttpOnly refresh cookie. Dedupes parallel 401s. */
export async function refreshAccessOnce(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = (async () => {
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
        });
        return res.ok;
      } catch {
        return false;
      } finally {
        refreshInFlight = null;
      }
    })();
  }
  return refreshInFlight;
}

function isAuthPath(url: string): boolean {
  return /\/auth\/(login|register|refresh|logout|google|forgot-password|reset-password|confirm-email)(?:\?|$)/.test(
    url,
  );
}

function emitSessionExpired(): void {
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
}

/**
 * Fetch that retries once after a silent refresh on 401.
 * Use for FormData uploads that cannot go through fetchApi.
 */
export async function fetchWithAuth(
  url: string,
  options?: RequestInit,
  retried = false,
): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
  });
  if (response.status === 401 && !retried && !isAuthPath(url)) {
    const ok = await refreshAccessOnce();
    if (ok) return fetchWithAuth(url, options, true);
    emitSessionExpired();
  }
  return response;
}

/**
 * Generic fetch function with error handling
 */
export async function fetchApi<T>(url: string, options?: RequestInit): Promise<T> {
  try {
    const response = await fetchWithAuth(url, options);

    if (response.status === 401) {
      // fetchWithAuth already attempted silent refresh
      if (!isAuthPath(url)) emitSessionExpired();
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Session expired');
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || errorData.detail || `HTTP error! Status: ${response.status}`,
      );
    }

    if (response.status === 204) {
      return {} as T;
    }

    return (await response.json()) as T;
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
}
